import { useEffect, useState } from "react";
import dayjs from "dayjs";
import clsx from "clsx";
import { db, eq } from "../lib/api";
import { invalidate, useQuery } from "../lib/hooks";
import { navigate } from "../lib/router";
import { useRefData } from "../lib/refdata";
import { useSession } from "../lib/session";
import { Field, Panel, useToast } from "./ui";
import { openPerson } from "./PersonDrawer";

type Line = { key: string; label: string; hint: string; actual: number; prev: number; target: number | null; money?: boolean;
  points?: number; points_prev?: number; points_target?: number | null };
type Gx = { month: string; end: string; share: number; days_left: number; lines: Line[];
  deals: { id: string; person_id: string; name: string; closed_on: string; premium: number | null; points: number | null }[] };

const fmt = (v: number | null | undefined, money?: boolean) => v == null ? "0" : money
  ? Number(v).toLocaleString("en-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 }) : Number(v).toLocaleString("en-CA");

/** GX: the monthly tracker (new prospects, recruits, premium + points, events attended). */
export function GxPanel() {
  const { can } = useSession();
  const [month, setMonth] = useState(() => dayjs().startOf("month").format("YYYY-MM-DD"));
  const q = useQuery(`gx:${month}`, () => db.rpc<Gx>("gx_progress", { p_month: month }), { staleMs: 120_000 });
  const [deals, setDeals] = useState(false);
  const g = q.data;
  const isNow = dayjs(month).isSame(dayjs(), "month");
  const shift = (n: number) => { setMonth(dayjs(month).add(n, "month").format("YYYY-MM-DD")); setDeals(false); };
  return (
    <Panel title={`GX · ${dayjs(month).format("MMMM YYYY")}`} hint={isNow && g ? `${g.days_left} day${g.days_left === 1 ? "" : "s"} left this month` : "Monthly tracker"}
      actions={<div className="row-inline" style={{ margin: 0, gap: 4 }}>
        <button className="btn small ghost" aria-label="Previous month" onClick={() => shift(-1)}>‹</button>
        {!isNow && <button className="btn small ghost" onClick={() => setMonth(dayjs().startOf("month").format("YYYY-MM-DD"))}>This month</button>}
        <button className="btn small ghost" aria-label="Next month" disabled={isNow} onClick={() => shift(1)}>›</button>
        {can("settings", "edit") && <button className="linkish small" onClick={() => navigate("/settings/goals")}>Targets</button>}</div>}>
      <div className="panel-body cycle">
        {!g ? <div className="small muted">Loading…</div> : g.lines.map((l) => {
          const t = Number(l.target) || 0;
          const pct = t ? Math.min(100, (l.actual / t) * 100) : 0;
          const exp = Math.min(100, g.share * 100);
          const status = !t ? "none" : l.actual >= t ? "done" : !isNow ? "behind" : l.actual >= t * g.share ? "ahead" : "behind";
          const pt = Number(l.points_target) || 0;
          return (
            <div key={l.key} className="cyc" title={l.hint}>
              <div className="cyc-h"><strong>{l.label}</strong>
                {status !== "none" && <span className={clsx("pill", status === "behind" ? "alert" : "warm")}>{status === "done" ? "Target hit" : status === "ahead" ? "On pace" : isNow ? "Behind pace" : "Missed"}</span>}</div>
              <div className="cyc-bar" aria-label={`${l.actual} of ${t}`}><i style={{ width: `${pct}%` }} className={status === "behind" ? "behind" : ""} />
                {isNow && t > 0 && <b style={{ left: `${exp}%` }} title="Where you should be by now" />}</div>
              <div className="small"><b>{fmt(l.actual, l.money)}</b>{t ? <> of {fmt(t, l.money)}</> : ""}
                {l.key === "premium" && <> · <b>{fmt(l.points)}</b> points{pt ? <> of {fmt(pt)}</> : ""}</>}</div>
              <div className="small muted">Last month {fmt(l.prev, l.money)}{l.key === "premium" ? ` · ${fmt(l.points_prev)} points` : ""}
                {isNow && t > 0 && status !== "done" ? ` · should be at ${fmt(Math.round(t * g.share), l.money)} by now` : ""}</div>
            </div>);
        })}
      </div>
      {g && (
        <div className="panel-body" style={{ paddingTop: 0 }}>
          <button className="linkish small" onClick={() => setDeals(!deals)}>{g.deals.length ? `${deals ? "Hide" : "Show"} business closed (${g.deals.length})` : "No business closed this month yet"}</button>
          {deals && g.deals.length > 0 && (
            <table className="data small" style={{ marginTop: 6 }}><thead><tr><th>Client</th><th>Closed</th><th style={{ textAlign: "right" }}>Premium</th><th style={{ textAlign: "right" }}>Points</th></tr></thead>
              <tbody>{g.deals.map((d) => (
                <tr key={d.id} onClick={() => openPerson(d.person_id)} style={{ cursor: "pointer" }}>
                  <td>{d.name}</td><td>{dayjs(d.closed_on).format("MMM D")}</td><td style={{ textAlign: "right" }}>{fmt(d.premium, true)}</td>
                  <td style={{ textAlign: "right" }}>{d.points == null ? <span className="pill alert" title="Open the deal and type the points">add points</span> : fmt(d.points)}</td></tr>))}</tbody></table>)}
        </div>)}
    </Panel>
  );
}

const FIELDS: [string, string, string][] = [
  ["prospects", "New prospects", "New contacts added to a lead list or as a client"], ["recruits", "Recruits", "People who join your team"],
  ["premium", "Premium ($)", "Business closed (Won) in the month"], ["points", "Points", "Points on the business closed (type them on each deal)"],
  ["events", "Events attended", "Events marked Attended"],
];
/** Settings → Goals: the GX monthly targets */
export function GxTargets() {
  const ref = useRefData();
  const { profile } = useSession();
  const toast = useToast();
  const edit = profile?.role_key === "owner" || profile?.role_key === "admin";
  const cur = (ref.data?.setting("gx_targets") ?? {}) as Record<string, number>;
  const [f, setF] = useState<Record<string, string> | null>(null);
  useEffect(() => { if (ref.data && !f) setF(Object.fromEntries(FIELDS.map(([k]) => [k, cur[k] != null ? String(cur[k]) : ""]))); }, [ref.data]); // eslint-disable-line react-hooks/exhaustive-deps
  async function save() {
    const value = Object.fromEntries(FIELDS.map(([k]) => [k, f?.[k] ? Number(f[k]) : 0]));
    await db.update("settings", { key: eq("gx_targets") }, { value });
    invalidate("ref:"); invalidate("gx:"); toast("GX targets saved");
  }
  return (
    <Panel title="GX: monthly targets" hint="Counted by itself each calendar month (1st to month-end) and shown on the Command Center and the weekly review.">
      <div className="panel-body">
        <div className="form-grid">{FIELDS.map(([k, l, h]) => (
          <Field key={k} label={l}><input className="input" inputMode="numeric" disabled={!edit} value={f?.[k] ?? ""} placeholder="0"
            onChange={(e) => setF({ ...(f ?? {}), [k]: e.target.value.replace(/[^\d.]/g, "") })} /><span className="small muted">{h}</span></Field>))}</div>
        {edit && <button className="btn primary" onClick={save}>Save targets</button>}
      </div>
    </Panel>
  );
}
